/**
 * ADMINISTRATION › SUPERVISION — LE MODÈLE DES ÉCRANS GÉNÉRÉS (21/10/2026).
 *
 * Sonde publie son schéma de réglages (`GET /v1/settings/schema`) : des
 * groupes (un écran chacun) et des réglages typés. Gestion n'en recopie
 * AUCUN : chaque écran se construit du schéma, un composant par TYPE (16
 * types). Un nouveau réglage de Sonde apparaît donc sans une ligne de code.
 *
 * Ce module est PUR (aucun React, aucun appel) : il porte les formes, les
 * gardes, la conversion valeur ⇄ brouillon de saisie, la validation (Gestion
 * refuse lui-même une valeur hors bornes avant d'appeler Sonde), le résumé
 * d'une valeur (« défaut : 45 s ») et le corps exact du PUT.
 *
 * Corps du PUT (contrat v1, `SettingsPutBody`) :
 *   { values: { clé: valeur }, scope?: { kind, ref }, reason?, reset?: [clés] }
 * « Remettre au défaut » passe TOUJOURS par `reset` : c'est le seul sens sans
 * ambiguïté, y compris pour un réglage `nullable` où `null` est une vraie valeur.
 */

/* ═══════════════ Formes de Sonde (contrat v1, relayées telles quelles) ═══════════════ */

export interface OptionSonde { value: string; label: string }

export type Sensibilite = false | 'always' | 'on_decrease'

export interface ReglagePublie {
  key: string
  group: string
  tab: string | null
  label: string
  help: string
  consequence: string
  type: string
  unit: string | null
  default: unknown
  defaults_by: Record<string, unknown>
  min: number | null
  max: number | null
  step: number | null
  floor: number | null
  options: OptionSonde[] | null
  map_keys: OptionSonde[] | null
  max_items: number | null
  item_min: number | null
  item_max: number | null
  direction: 'above' | 'below' | null
  for_bounds: { min: number; max: number } | null
  nullable: boolean
  sensitive: Sensibilite
  scopes: string[]
  fixed: boolean
  truth: string
  version: number
  order: number
  declared_by: string
}

export interface GroupeSchema {
  code: string
  screen: number
  label: string
  description: string
  tabs: OptionSonde[]
  revision: number
  etag: string
}

export interface SchemaReglages { groups: GroupeSchema[]; settings: ReglagePublie[] }

export interface ValeurReglage {
  key: string
  group: string
  scope_kind: string
  scope_ref: string
  value: unknown
  is_default: boolean
  default: unknown
  updated_at: string | null
  updated_by: string | null
}

export interface ValeursReglages {
  groups: { code: string; revision: number; etag: string }[]
  values: ValeurReglage[]
}

/** Les 16 types du contrat. Un type inconnu se règle en texte JSON validé. */
export const TYPES_REGLAGE = [
  'boolean', 'integer', 'number', 'enum', 'string', 'url', 'url_list', 'email_list', 'string_list',
  'integer_list', 'time_of_day', 'weekly_windows', 'threshold', 'datetime', 'enum_map', 'json',
] as const
export type TypeReglage = typeof TYPES_REGLAGE[number]
export const estTypeConnu = (t: string): t is TypeReglage => (TYPES_REGLAGE as readonly string[]).includes(t)

/* ═══════════════ Gardes ═══════════════ */

const objet = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)
const estOption = (x: unknown): x is OptionSonde => objet(x) && typeof x['value'] === 'string' && typeof x['label'] === 'string'
const optionsOuNul = (x: unknown) => x === null || x === undefined || (Array.isArray(x) && x.every(estOption))
const nombreOuNul = (x: unknown) => x === null || x === undefined || typeof x === 'number'

export function estReglage(x: unknown): x is ReglagePublie {
  return objet(x) && typeof x['key'] === 'string' && typeof x['group'] === 'string' && typeof x['label'] === 'string'
    && typeof x['type'] === 'string' && Array.isArray(x['scopes']) && typeof x['fixed'] === 'boolean'
    && optionsOuNul(x['options']) && optionsOuNul(x['map_keys'])
    && nombreOuNul(x['min']) && nombreOuNul(x['max'])
}

export function estGroupe(x: unknown): x is GroupeSchema {
  return objet(x) && typeof x['code'] === 'string' && typeof x['label'] === 'string'
    && typeof x['etag'] === 'string' && Array.isArray(x['tabs']) && (x['tabs'] as unknown[]).every(estOption)
}

export function estSchema(x: unknown): x is SchemaReglages {
  return objet(x) && Array.isArray(x['groups']) && (x['groups'] as unknown[]).every(estGroupe)
    && Array.isArray(x['settings']) && (x['settings'] as unknown[]).every(estReglage)
}

export function estValeurs(x: unknown): x is ValeursReglages {
  return objet(x) && Array.isArray(x['groups']) && Array.isArray(x['values'])
    && (x['groups'] as unknown[]).every((g) => objet(g) && typeof g['code'] === 'string' && typeof g['etag'] === 'string')
    && (x['values'] as unknown[]).every((v) => objet(v) && typeof v['key'] === 'string' && typeof v['is_default'] === 'boolean')
}

/** Réglages d'un groupe, dans l'ordre de Sonde (`order`, puis clé). */
export function reglagesDuGroupe(schema: SchemaReglages, groupe: string): ReglagePublie[] {
  return schema.settings.filter((s) => s.group === groupe)
    .sort((a, b) => (a.order - b.order) || a.key.localeCompare(b.key))
}

/** Onglets réellement peuplés (un onglet vide ne s'affiche pas ; « Autres » recueille les sans-onglet). */
export function ongletsDuGroupe(groupe: GroupeSchema, reglages: ReglagePublie[]): OptionSonde[] {
  if (groupe.tabs.length === 0) return []
  const peuples = groupe.tabs.filter((t) => reglages.some((s) => s.tab === t.value))
  const orphelins = reglages.some((s) => !s.tab || !groupe.tabs.some((t) => t.value === s.tab))
  return orphelins ? [...peuples, { value: '', label: 'Autres' }] : peuples
}

export function dansOnglet(s: ReglagePublie, onglet: string, groupe: GroupeSchema): boolean {
  if (groupe.tabs.length === 0) return true
  if (onglet === '') return !s.tab || !groupe.tabs.some((t) => t.value === s.tab)
  return s.tab === onglet
}

/* ═══════════════ Comparaison stable ═══════════════ */

function stable(x: unknown): unknown {
  if (Array.isArray(x)) return x.map(stable)
  if (objet(x)) return Object.fromEntries(Object.keys(x).sort().map((k) => [k, stable(x[k])]))
  return x
}
export const egal = (a: unknown, b: unknown): boolean => JSON.stringify(stable(a ?? null)) === JSON.stringify(stable(b ?? null))

/** Même valeur pour ce réglage : un instant se compare en instant (« …:00Z » = « …:00.000Z »). */
export function memeValeur(s: ReglagePublie, a: unknown, b: unknown): boolean {
  if (s.type === 'datetime' && typeof a === 'string' && typeof b === 'string') {
    const x = Date.parse(a), y = Date.parse(b)
    if (!Number.isNaN(x) && !Number.isNaN(y)) return x === y
  }
  return egal(a, b)
}

/* ═══════════════ Heures de Paris ═══════════════ */

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/
export const estHeure = (v: string) => HHMM.test(v)
export const minutesDe = (v: string): number => { const [h, m] = v.split(':').map(Number) as [number, number]; return h * 60 + m }
export const heureDe = (n: number): string => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`

/** Décalage de Paris (minutes) à un instant donné. */
function decalageParis(instant: number): number {
  const p = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Paris', hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(instant))
  const v = (t: string) => Number(p.find((x) => x.type === t)?.value)
  const local = Date.UTC(v('year'), v('month') - 1, v('day'), v('hour'), v('minute'), v('second'))
  return Math.round((local - Math.floor(instant / 1000) * 1000) / 60_000)
}

/** ISO → « AAAA-MM-JJTHH:MM » à l'heure de Paris (champ datetime-local). */
export function isoVersParis(iso: string | null | undefined): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return ''
  const d = new Date(t + decalageParis(t) * 60_000)
  return d.toISOString().slice(0, 16)
}

/** « AAAA-MM-JJTHH:MM » lu à l'heure de Paris → ISO UTC ; null si illisible. */
export function parisVersIso(local: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local.trim())
  if (!m) return null
  const naif = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]))
  // Deux passes : le décalage dépend de l'instant (heure d'été).
  let t = naif - decalageParis(naif) * 60_000
  t = naif - decalageParis(t) * 60_000
  return Number.isNaN(t) ? null : new Date(t).toISOString()
}

export const fmtDateParis = (iso: string): string => {
  const t = Date.parse(iso)
  return Number.isNaN(t) ? iso : new Date(t).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
  })
}

/* ═══════════════ Fenêtres hebdomadaires ═══════════════ */

export interface Fenetre { days: number[]; start: string; end: string }
export const JOURS_COURTS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'] as const
export const JOURS_LONGS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'] as const

export const estFenetre = (x: unknown): x is Fenetre => objet(x) && Array.isArray(x['days'])
  && (x['days'] as unknown[]).every((d) => typeof d === 'number') && typeof x['start'] === 'string' && typeof x['end'] === 'string'

/** Fin ≤ début : la fenêtre se termine le lendemain. */
export const finLendemain = (f: Fenetre): boolean => estHeure(f.start) && estHeure(f.end) && minutesDe(f.end) <= minutesDe(f.start)

/** Plages peintes par jour ISO (1..7), une fenêtre de nuit coupée à minuit et reportée au lendemain. */
export function plagesParJour(fenetres: Fenetre[]): Map<number, [number, number][]> {
  const r = new Map<number, [number, number][]>()
  for (let j = 1; j <= 7; j++) r.set(j, [])
  for (const f of fenetres) {
    if (!estHeure(f.start) || !estHeure(f.end)) continue
    const a = minutesDe(f.start), b = minutesDe(f.end)
    for (const j of f.days) {
      if (j < 1 || j > 7) continue
      if (b > a) r.get(j)!.push([a, b])
      else {
        r.get(j)!.push([a, 1440])
        if (b > 0) r.get(j === 7 ? 1 : j + 1)!.push([0, b])
      }
    }
  }
  return r
}

/** « mer → sam, 23:00 → 06:30 le lendemain » */
export function phraseFenetre(f: Fenetre): string {
  const jours = [...f.days].sort((a, b) => a - b)
  const contigus = jours.length > 2 && jours.every((d, i) => i === 0 || d === jours[i - 1]! + 1)
  const j = jours.length === 7 ? 'tous les jours'
    : contigus ? `${JOURS_LONGS[jours[0]! - 1]} → ${JOURS_LONGS[jours[jours.length - 1]! - 1]}`
      : jours.map((d) => JOURS_LONGS[d - 1] ?? String(d)).join(', ')
  return `${j || 'aucun jour'}, ${f.start} → ${f.end}${finLendemain(f) ? ' le lendemain' : ''}`
}

/* ═══════════════ Seuils ═══════════════ */

export interface Seuil { warn: number | null; crit: number | null; for_s?: number | null }
export const estSeuil = (x: unknown): x is Seuil => objet(x) && nombreOuNul(x['warn']) && nombreOuNul(x['crit'])

export const phraseSens = (d: 'above' | 'below' | null): string => d === 'below' ? 'en dessous de' : 'au-dessus de'

/* ═══════════════ Réglages json : éditeurs dédiés ═══════════════ */

export type GenreCle = 'zone' | 'texte'
export type GenreValeur = 'texte' | 'entier' | 'zone' | 'zone_ou_externe' | 'choix' | 'temoin'
export interface ConfigDico {
  cle: string
  valeur: string
  genreCle: GenreCle
  genreValeur: GenreValeur
  options?: OptionSonde[]
  unite?: string
  ajout: string
}

/** Les 5 réglages `json` du schéma : un tableau clé → valeur dédié. */
export const JSON_DEDIES: Record<string, ConfigDico> = {
  'zones.power_witness': { cle: 'Salle', valeur: 'Témoin d’alimentation', genreCle: 'zone', genreValeur: 'temoin', ajout: 'Ajouter une salle' },
  'registry.referentiels_parc_rooms': { cle: 'Salle de Parc', valeur: 'Zone de Sonde', genreCle: 'texte', genreValeur: 'zone_ou_externe', ajout: 'Ajouter une salle de Parc' },
  'schedule.zone_profiles': {
    cle: 'Salle', valeur: 'Profil', genreCle: 'zone', genreValeur: 'choix', ajout: 'Ajouter une salle',
    options: [{ value: 'room', label: 'Soirée (selon la salle)' }, { value: 'always', label: '24 h/24' }, { value: 'office', label: 'Bureau' }],
  },
  'schedule.referentiels_zone_parents': { cle: 'Zone', valeur: 'Ouvre avec la salle', genreCle: 'zone', genreValeur: 'zone', ajout: 'Rattacher une zone' },
  'thresholds.server.serveur_link_speeds': { cle: 'Carte réseau', valeur: 'Vitesse normale', genreCle: 'texte', genreValeur: 'entier', unite: 'Mbit/s', ajout: 'Ajouter une carte' },
}

const estDicoPlat = (x: unknown): x is Record<string, string | number> =>
  objet(x) && Object.values(x).every((v) => typeof v === 'string' || (typeof v === 'number' && Number.isFinite(v)))

/* ═══════════════ Brouillons de saisie ═══════════════ */

export interface LigneDico { cle: string; valeur: string }
export type Brouillon =
  | { t: 'bool'; v: boolean }
  | { t: 'texte'; v: string }
  | { t: 'liste'; v: string[] }
  | { t: 'fenetres'; v: Fenetre[] }
  | { t: 'seuil'; warn: string; crit: string; for_s: string }
  | { t: 'carte'; v: Record<string, string> }
  | { t: 'dico'; v: LigneDico[] }
  | { t: 'json'; v: string }

const nombreTexte = (x: unknown): string => typeof x === 'number' && Number.isFinite(x) ? String(x) : ''

/** Mode d'édition d'un réglage : le type, et pour `json` l'éditeur dédié s'il s'applique. */
export function modeDe(s: ReglagePublie, valeur: unknown): TypeReglage | 'json_dico' | 'json_texte' {
  if (!estTypeConnu(s.type)) return 'json_texte'
  if (s.type === 'json') {
    const ok = JSON_DEDIES[s.key] && (valeur === null || valeur === undefined || estDicoPlat(valeur)) && estDicoPlat(s.default ?? {})
    return ok ? 'json_dico' : 'json_texte'
  }
  return s.type
}

export function brouillonDe(s: ReglagePublie, valeur: unknown): Brouillon {
  const mode = modeDe(s, valeur)
  switch (mode) {
    case 'boolean': return { t: 'bool', v: valeur === true }
    case 'integer': case 'number': return { t: 'texte', v: nombreTexte(valeur) }
    case 'enum': case 'string': case 'url': case 'time_of_day':
      return { t: 'texte', v: typeof valeur === 'string' ? valeur : '' }
    case 'datetime': return { t: 'texte', v: typeof valeur === 'string' ? isoVersParis(valeur) : '' }
    case 'url_list': case 'email_list': case 'string_list':
      return { t: 'liste', v: Array.isArray(valeur) ? valeur.filter((x): x is string => typeof x === 'string') : [] }
    case 'integer_list':
      return { t: 'liste', v: Array.isArray(valeur) ? valeur.filter((x): x is number => typeof x === 'number').map(String) : [] }
    case 'weekly_windows':
      return { t: 'fenetres', v: Array.isArray(valeur) ? valeur.filter(estFenetre).map((f) => ({ days: [...f.days], start: f.start, end: f.end })) : [] }
    case 'threshold': {
      const v = estSeuil(valeur) ? valeur : { warn: null, crit: null }
      return { t: 'seuil', warn: nombreTexte(v.warn), crit: nombreTexte(v.crit), for_s: nombreTexte(v.for_s) }
    }
    case 'enum_map':
      return { t: 'carte', v: objet(valeur) ? Object.fromEntries(Object.entries(valeur).filter(([, x]) => typeof x === 'string')) as Record<string, string> : {} }
    case 'json_dico':
      return { t: 'dico', v: Object.entries(estDicoPlat(valeur) ? valeur : {}).map(([cle, x]) => ({ cle, valeur: String(x) })) }
    default:
      return { t: 'json', v: valeur === undefined ? '' : JSON.stringify(valeur, null, 2) }
  }
}

export type Lu = { ok: true; value: unknown } | { ok: false; message: string }
const refus = (message: string): Lu => ({ ok: false, message })
const accepte = (value: unknown): Lu => ({ ok: true, value })

/** Plancher effectif : le plus haut de `min` et `floor`. */
export const minEffectif = (s: ReglagePublie): number | null =>
  s.min === null ? s.floor : s.floor === null ? s.min : Math.max(s.min, s.floor)

const unite = (s: ReglagePublie) => s.unit ? ` ${s.unit}` : ''

/** Nombre saisi (virgule française acceptée) ; null si vide, NaN si illisible. */
export function lireNombre(texte: string): number | null {
  const t = texte.trim().replace(/\s/g, '').replace(',', '.')
  if (t === '') return null
  return /^-?\d+(\.\d+)?$/.test(t) ? Number(t) : Number.NaN
}

function bornes(s: ReglagePublie, n: number, entier: boolean, min = minEffectif(s), max = s.max, nom = ''): string | null {
  if (!Number.isFinite(n)) return `${nom}nombre attendu.`
  if (entier && !Number.isInteger(n)) return `${nom}nombre entier attendu.`
  if (min !== null && n < min) return `${nom}au moins ${min.toLocaleString('fr-FR')}${unite(s)}${s.floor !== null && min === s.floor ? ' (plancher non réglable)' : ''}.`
  if (max !== null && n > max) return `${nom}au plus ${max.toLocaleString('fr-FR')}${unite(s)}.`
  return null
}

function urlValide(v: string): boolean {
  try {
    const u = new URL(v)
    return ['http:', 'https:', 'ws:', 'wss:'].includes(u.protocol) && !u.username && !u.password
  } catch { return false }
}
const COURRIEL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/** Brouillon → valeur à envoyer, validée des deux côtés (bornes, format, doublons). */
export function lireBrouillon(s: ReglagePublie, b: Brouillon): Lu {
  const mode = modeDe(s, s.default)
  const vide = (): Lu => s.nullable ? accepte(null) : refus('valeur requise.')
  switch (b.t) {
    case 'bool': return accepte(b.v)
    case 'texte': {
      const v = b.v.trim()
      if (s.type === 'integer' || s.type === 'number') {
        const n = lireNombre(v)
        if (n === null) return vide()
        const e = bornes(s, n, s.type === 'integer')
        return e ? refus(e) : accepte(n)
      }
      if (v === '') return s.type === 'string' && !s.nullable ? accepte('') : vide()
      if (s.type === 'enum') return (s.options ?? []).some((o) => o.value === v) ? accepte(v) : refus('choix inconnu.')
      if (s.type === 'url') return urlValide(v) ? accepte(v) : refus('adresse http(s) attendue, sans identifiant ni mot de passe.')
      if (s.type === 'time_of_day') {
        if (!estHeure(v)) return refus('heure attendue au format HH:MM.')
        const m = minutesDe(v)
        if (s.min !== null && m < s.min) return refus(`au plus tôt ${heureDe(s.min)}.`)
        if (s.max !== null && m > s.max) return refus(`au plus tard ${heureDe(s.max)}.`)
        return accepte(v)
      }
      if (s.type === 'datetime') {
        const iso = parisVersIso(v)
        return iso ? accepte(iso) : refus('date et heure attendues.')
      }
      return v.length > 500 ? refus('500 caractères au plus.') : accepte(v)
    }
    case 'liste': {
      const items = b.v.map((x) => x.trim()).filter(Boolean)
      if (s.max_items !== null && items.length > s.max_items) return refus(`${s.max_items} éléments au plus.`)
      if (s.type === 'integer_list') {
        const ns: number[] = []
        for (const it of items) {
          const n = lireNombre(it)
          if (n === null || !Number.isInteger(n)) return refus(`« ${it} » : nombre entier attendu.`)
          if (s.item_min !== null && n < s.item_min) return refus(`« ${it} » : au moins ${s.item_min}.`)
          if (s.item_max !== null && n > s.item_max) return refus(`« ${it} » : au plus ${s.item_max}.`)
          ns.push(n)
        }
        if (new Set(ns).size !== ns.length) return refus('valeurs en double.')
        return accepte(ns)
      }
      const cles = items.map((x) => x.toLowerCase())
      if (new Set(cles).size !== cles.length) return refus('valeurs en double.')
      if (s.type === 'url_list') {
        const faux = items.find((x) => !urlValide(x))
        return faux ? refus(`« ${faux} » : adresse http(s) attendue.`) : accepte(items)
      }
      if (s.type === 'email_list') {
        const faux = items.find((x) => !COURRIEL.test(x) || x.length > 254)
        return faux ? refus(`« ${faux} » : adresse e-mail invalide.`) : accepte(items)
      }
      if (s.options && s.options.length > 0) {
        const inconnu = items.find((x) => !s.options!.some((o) => o.value === x))
        return inconnu ? refus(`« ${inconnu} » : choix inconnu.`) : accepte(items)
      }
      const long = items.find((x) => x.length > 200)
      return long ? refus('200 caractères au plus par élément.') : accepte(items)
    }
    case 'fenetres': {
      if (s.max_items !== null && b.v.length > s.max_items) return refus(`${s.max_items} créneaux au plus.`)
      for (const [i, f] of b.v.entries()) {
        const n = `créneau ${i + 1} : `
        if (f.days.length === 0) return refus(`${n}choisir au moins un jour.`)
        if (!estHeure(f.start) || !estHeure(f.end)) return refus(`${n}heures attendues au format HH:MM.`)
        if (f.start === f.end) return refus(`${n}le début et la fin sont identiques.`)
      }
      return accepte(b.v.map((f) => ({ days: [...new Set(f.days)].sort((a, c) => a - c), start: f.start, end: f.end })))
    }
    case 'seuil': {
      const warn = lireNombre(b.warn), crit = lireNombre(b.crit)
      for (const [n, nom] of [[warn, 'avertissement : '], [crit, 'critique : ']] as const) {
        if (n === null) continue
        const e = bornes(s, n, false, s.min, s.max, nom)
        if (e) return refus(e)
      }
      if (warn === null && crit === null) return refus('au moins un des deux seuils.')
      if (warn !== null && crit !== null) {
        if (s.direction === 'below' ? warn < crit : warn > crit) {
          return refus(s.direction === 'below'
            ? 'le seuil d’avertissement doit être supérieur ou égal au seuil critique.'
            : 'le seuil d’avertissement doit être inférieur ou égal au seuil critique.')
        }
      }
      const v: Seuil = { warn, crit }
      if (s.for_bounds) {
        const f = lireNombre(b.for_s)
        if (f !== null) {
          if (!Number.isInteger(f)) return refus('durée : nombre entier de secondes.')
          if (f < s.for_bounds.min || f > s.for_bounds.max) return refus(`durée : entre ${s.for_bounds.min} et ${s.for_bounds.max} s.`)
        }
        v.for_s = f
      }
      return accepte(v)
    }
    case 'carte': {
      const cles = new Set((s.map_keys ?? []).map((k) => k.value))
      const vals = new Set((s.options ?? []).map((o) => o.value))
      const out: Record<string, string> = {}
      for (const [k, x] of Object.entries(b.v)) {
        if (!x) continue
        if (!cles.has(k)) return refus(`« ${k} » : clé inconnue.`)
        if (!vals.has(x)) return refus(`« ${k} » : choix inconnu.`)
        out[k] = x
      }
      return accepte(out)
    }
    case 'dico': {
      const conf = JSON_DEDIES[s.key]
      const out: Record<string, string | number> = {}
      for (const l of b.v) {
        const k = l.cle.trim(), x = l.valeur.trim()
        if (!k && !x) continue
        if (!k) return refus(`${conf?.cle ?? 'clé'} manquante.`)
        if (k in out) return refus(`« ${k} » en double.`)
        if (!x) return refus(`« ${k} » : ${conf?.valeur.toLowerCase() ?? 'valeur'} manquante.`)
        if (conf?.genreValeur === 'entier') {
          const n = lireNombre(x)
          if (n === null || !Number.isInteger(n) || n <= 0) return refus(`« ${k} » : nombre entier positif attendu.`)
          out[k] = n
        } else if (conf?.genreValeur === 'choix' && conf.options && !conf.options.some((o) => o.value === x)) {
          return refus(`« ${k} » : choix inconnu.`)
        } else out[k] = x
      }
      return accepte(out)
    }
    case 'json': {
      const t = b.v.trim()
      if (t === '') return vide()
      try { return accepte(JSON.parse(t)) } catch { return refus(mode === 'json_texte' ? 'texte JSON invalide.' : 'JSON invalide.') }
    }
  }
}

/* ═══════════════ Résumé d'une valeur (« défaut : X ») ═══════════════ */

const libelleOption = (opts: OptionSonde[] | null, v: string) => opts?.find((o) => o.value === v)?.label ?? v

export function fmtValeur(s: ReglagePublie, v: unknown): string {
  if (v === null || v === undefined) return s.nullable ? 'aucune valeur' : '—'
  switch (s.type) {
    case 'boolean': return v === true ? 'Activé' : 'Désactivé'
    case 'integer': case 'number':
      return typeof v === 'number' ? `${v.toLocaleString('fr-FR')}${unite(s)}` : '—'
    case 'enum': return typeof v === 'string' ? libelleOption(s.options, v) : '—'
    case 'datetime': return typeof v === 'string' ? `${fmtDateParis(v)} (heure de Paris)` : '—'
    case 'url_list': case 'email_list': case 'string_list': case 'integer_list': {
      if (!Array.isArray(v)) return '—'
      if (v.length === 0) return 'aucun'
      if (s.type === 'integer_list' && s.item_min === 1 && s.item_max === 7) {
        return v.length === 7 ? 'tous les jours' : v.map((d) => JOURS_COURTS[Number(d) - 1] ?? String(d)).join(', ')
      }
      const items = v.map((x) => typeof x === 'string' ? libelleOption(s.options, x) : String(x))
      return items.length > 3 ? `${items.slice(0, 3).join(', ')} et ${items.length - 3} de plus` : items.join(', ')
    }
    case 'weekly_windows':
      if (!Array.isArray(v)) return '—'
      return v.length === 0 ? 'aucun créneau' : v.filter(estFenetre).map(phraseFenetre).join(' ; ')
    case 'threshold': {
      if (!estSeuil(v)) return '—'
      const u = unite(s)
      const parts = [
        v.warn !== null ? `avertissement ${v.warn.toLocaleString('fr-FR')}${u}` : null,
        v.crit !== null ? `critique ${v.crit.toLocaleString('fr-FR')}${u}` : null,
        typeof v.for_s === 'number' ? `pendant ${v.for_s} s` : null,
      ].filter(Boolean)
      return parts.join(' · ') || '—'
    }
    case 'enum_map': {
      if (!objet(v)) return '—'
      const n = Object.keys(v).length
      return `${n} ligne${n > 1 ? 's' : ''}`
    }
    case 'json': {
      if (objet(v)) { const n = Object.keys(v).length; return `${n} entrée${n > 1 ? 's' : ''}` }
      return JSON.stringify(v).slice(0, 80)
    }
    default:
      return typeof v === 'string' ? v : JSON.stringify(v).slice(0, 80)
  }
}

/* ═══════════════ Source de vérité, step-up ═══════════════ */

export const VERITE: Record<string, { lettre: string; texte: string }> = {
  S: { lettre: 'S', texte: 'Réglé dans Sonde' },
  G: { lettre: 'G', texte: 'Vient de Gestion' },
  C: { lettre: 'C', texte: 'Fixé par le fichier compose du serveur' },
  E: { lettre: 'É', texte: 'Lu sur l’équipement' },
  'É': { lettre: 'É', texte: 'Lu sur l’équipement' },
}

/** Le changement demandera-t-il une confirmation d'identité (Sonde décide, Gestion prévient) ? */
export function demandeStepUp(s: ReglagePublie, avant: unknown, apres: unknown): boolean {
  if (s.sensitive === 'always') return true
  if (s.sensitive === 'on_decrease') {
    if (typeof avant === 'number' && typeof apres === 'number') return apres < avant
    return avant === null && typeof apres === 'number'
  }
  return false
}

/* ═══════════════ Portées (surcharges) ═══════════════ */

export type GenrePortee = 'family' | 'zone' | 'device'
export interface Portee { kind: GenrePortee; ref: string }
export const LIBELLE_PORTEE: Record<GenrePortee, string> = { family: 'Famille', zone: 'Salle ou zone', device: 'Appareil' }

/** Portées de surcharge proposées par un groupe (hors `global` et `source`). */
export function porteesDuGroupe(reglages: ReglagePublie[]): GenrePortee[] {
  const k = new Set(reglages.flatMap((s) => s.scopes))
  return (['family', 'zone', 'device'] as const).filter((x) => k.has(x))
}

const REF = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,199}$/
/** `?portee=zone:R1` → portée ; tout le reste → globale. */
export function lirePortee(texte: string | null | undefined): Portee | null {
  const m = /^(family|zone|device):(.+)$/.exec(texte ?? '')
  return m && REF.test(m[2]!) ? { kind: m[1] as GenrePortee, ref: m[2]! } : null
}
export const estRefPortee = (v: string) => REF.test(v)

/** Familles connues de Sonde : les clés des réglages « par famille » du schéma. */
export function famillesDuSchema(schema: SchemaReglages): OptionSonde[] {
  const vu = new Map<string, string>()
  for (const s of schema.settings) for (const k of s.map_keys ?? []) if (!vu.has(k.value)) vu.set(k.value, k.label)
  return [...vu].map(([value, label]) => ({ value, label }))
}

/* ═══════════════ Le corps du PUT ═══════════════ */

export interface Edition {
  brouillons: Record<string, Brouillon>
  /** Clés marquées « Remettre au défaut ». */
  remises: string[]
}

export interface Corps {
  values: Record<string, unknown>
  reset: string[]
  /** Clés réellement modifiées (valeurs + remises). */
  cles: string[]
  /** Première erreur de saisie, « Libellé : message » ; null si tout est lisible. */
  erreur: string | null
  /** Au moins une clé demandera une confirmation d'identité. */
  stepUp: boolean
}

/**
 * Ce qui part vers Sonde : seulement les clés modifiées (comparées à la valeur
 * du serveur), jamais un réglage non réglable, les remises dans `reset`.
 */
export function construireCorps(reglages: ReglagePublie[], serveur: Map<string, ValeurReglage>, e: Edition): Corps {
  const parCle = new Map(reglages.map((s) => [s.key, s]))
  const values: Record<string, unknown> = {}
  const reset: string[] = []
  let erreur: string | null = null
  let stepUp = false
  for (const k of e.remises) {
    const s = parCle.get(k), v = serveur.get(k)
    if (!s || s.fixed || !v || v.is_default) continue
    reset.push(k)
    if (demandeStepUp(s, v.value, v.default)) stepUp = true
  }
  for (const [k, b] of Object.entries(e.brouillons)) {
    const s = parCle.get(k), v = serveur.get(k)
    if (!s || s.fixed || !v || reset.includes(k)) continue
    const lu = lireBrouillon(s, b)
    if (!lu.ok) { erreur ??= `${s.label} : ${lu.message}`; continue }
    if (memeValeur(s, lu.value, v.value)) continue
    values[k] = lu.value
    if (demandeStepUp(s, v.value, lu.value)) stepUp = true
  }
  return { values, reset, cles: [...Object.keys(values), ...reset], erreur, stepUp }
}

/** Corps exact du contrat : `reset` absent quand il est vide. */
export function corpsPut(c: Corps, portee: Portee | null, motif?: string): Record<string, unknown> {
  return {
    values: c.values,
    ...(c.reset.length > 0 ? { reset: c.reset } : {}),
    ...(portee ? { scope: { kind: portee.kind, ref: portee.ref } } : {}),
    ...(motif && motif.trim() ? { reason: motif.trim().slice(0, 500) } : {}),
  }
}

/** Le filtre de la page : libellé, clé, aide (sans accents ni casse). */
export function correspond(s: ReglagePublie, filtre: string): boolean {
  const n = (t: string) => t.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
  const f = n(filtre.trim())
  return !f || n(`${s.label} ${s.key} ${s.help}`).includes(f)
}
